import styled from "styled-components";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";

import { PageHeading, TeamHeading } from "@/components/Layout/Parts";
import OptimisationTable from "./components/OptimisationTable";
import { TableHeader } from "@/components/Table";
import { Controls, ControlsButton } from "@/components/Controls";
import Button from "@/components/Button";
import OptimisationTestFormModal from "./components/OptimisationProjectFormModal";
import OptimisationYoutubeQuota from "./components/OptimisationYoutubeQuota";
import {
  OptimisationProjectNetworkEnum,
  OptimisationProjectStatusEnum,
  OptimisationProjectTypeEnum,
} from "@/utils/enums";
import { RootState } from "@/utils/store";
import { selectTeamTestingProperties } from "@/selectors";
import InvalidSettingsAlert from "./components/InvalidSettingsAlert";
import { OptimisationProjectResource } from "@/types/resources";
import { api } from "@/utils/api";
import OptimisationOverviewNotifications from "./components/OptimisationOverviewNotifications";
import OptimisationProjectsSearch from "./components/OptimisationProjectsSearch";
import EndedProjects from "./components/EndedProjects";

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const NewProjectButton = styled(Button)`
  &:disabled {
    background: ${({ theme }) => theme.colors.lightGray};
  }
`;

const tabs = [
  { value: OptimisationProjectNetworkEnum.YOUTUBE, title: "Youtube" },
  { value: OptimisationProjectNetworkEnum.FACEBOOK, title: "Facebook" },
];

const Optimisation = () => {
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const [showNewTestModal, setShowNewTestModal] = useState(false);

  const { teamId } = useParams();

  const {
    data: projects,
    isError,
    isLoading,
  } = useQuery<OptimisationProjectResource[]>(
    [
      "optimisation_tests",
      {
        teamId,
        activeTab: activeTab.value,
        status: [
          OptimisationProjectStatusEnum.DRAFT,
          OptimisationProjectStatusEnum.RUNNING,
        ].join(","),
      },
    ],
    () =>
      api.get<never, OptimisationProjectResource[]>(`/v1/optimisation-tests`, {
        params: {
          "filter[team_id]": teamId,
          "filter[type]": OptimisationProjectTypeEnum.OWN,
          "filter[network]": activeTab.value,
          "filter[status]": [
            OptimisationProjectStatusEnum.DRAFT,
            OptimisationProjectStatusEnum.RUNNING,
          ].join(","),
        },
      }),
    {
      staleTime: 60 * 1000,
    }
  );

  const { testingSettings, testingChannels } = useSelector((state: RootState) =>
    selectTeamTestingProperties(state, teamId!)
  );

  const ensureYoutubeSettingsIsSet = () => {
    if (!testingChannels?.length) {
      return false;
    }

    // Check whether google ads account is set
    if (!testingSettings?.google_ads_client_id) {
      return false;
    }

    return true;
  };

  const ensureFacebookSettingsIsSet = () => {
    return !!testingSettings?.facebook_authorized;
  };

  const isTestingSettingsSet = (() => {
    if (activeTab.value === OptimisationProjectNetworkEnum.YOUTUBE) {
      return ensureYoutubeSettingsIsSet();
    }

    return ensureFacebookSettingsIsSet();
  })();

  const draftProjects = useMemo(
    () =>
      projects?.filter((p) => p.status === OptimisationProjectStatusEnum.DRAFT),
    [projects]
  );

  const runningProjects = useMemo(
    () =>
      projects?.filter(
        (p) => p.status === OptimisationProjectStatusEnum.RUNNING
      ),
    [projects]
  );

  const projectsIds = useMemo(
    () => projects?.map((p) => p.id)?.join(",") ?? "",
    [projects]
  );

  return (
    <>
      <TeamHeading />
      <HeadingRow>
        <PageHeading>Optimisation Overview</PageHeading>
        <NewProjectButton
          onClick={() => setShowNewTestModal((prev) => !prev)}
          disabled={!isTestingSettingsSet}
        >
          New project
        </NewProjectButton>
      </HeadingRow>
      <TableHeader>
        <Controls>
          {tabs.map((tab, index) => (
            <ControlsButton
              key={index}
              active={tab.value === activeTab.value}
              onClick={() => setActiveTab(tab)}
            >
              {tab.title}
            </ControlsButton>
          ))}
        </Controls>
      </TableHeader>
      {!isTestingSettingsSet && (
        <InvalidSettingsAlert network={activeTab.value} />
      )}
      {activeTab.value === OptimisationProjectNetworkEnum.YOUTUBE && (
        <OptimisationYoutubeQuota />
      )}
      <OptimisationProjectsSearch network={activeTab.value} teamId={teamId!} />
      <OptimisationOverviewNotifications projectsIds={projectsIds} />
      <OptimisationTable
        projects={draftProjects}
        isLoading={isLoading}
        isError={isError}
        status={OptimisationProjectStatusEnum.DRAFT}
      />
      <OptimisationTable
        projects={runningProjects}
        isLoading={isLoading}
        isError={isError}
        status={OptimisationProjectStatusEnum.RUNNING}
      />
      <EndedProjects teamId={teamId} network={activeTab.value} />
      <AnimatePresence>
        {showNewTestModal && (
          <OptimisationTestFormModal
            setActiveTab={setActiveTab}
            onClose={() => setShowNewTestModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Optimisation;
